import React from 'react';
import { Box, Typography } from '@mui/material';
import { RichTextViewer } from '../Common/RichTextViewer';
import { OBSIDIAN, OBSIDIAN_BODY_FONT } from '../../constants/obsidianDashboardTheme';
import { plainTextFromHtml } from '../../pages/BrandDashboard/contestWizard/richTextUtils';

type Props = {
    content?: string | null;
    fallback?: string;
    fontSize?: number;
    fontWeight?: number | string;
    color?: string;
};

export function SandboxRichText({
    content,
    fallback,
    fontSize = 13,
    fontWeight = 400,
    color = OBSIDIAN.onSurfaceVariant,
}: Props) {
    const html = String(content || '').trim();
    if (html && plainTextFromHtml(html)) {
        return (
            <Box
                sx={{
                    '& .rich-text-wrapper': { width: '100%' },
                    '& .rich-text-wrapper .ql-editor': {
                        p: '0 !important',
                        m: 0,
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize,
                        fontWeight,
                        lineHeight: 1.7,
                        color,
                        overflow: 'visible',
                    },
                    /* RichTextViewer wraps HTML in an extra color div */
                    '& .ql-editor > div': {
                        p: 0,
                        m: 0,
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        lineHeight: 'inherit',
                        color: 'inherit',
                    },
                    '& .ql-editor p, & .ql-editor .thspian-lexical-p': {
                        m: 0,
                        mb: 1.5,
                        fontWeight,
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                    },
                    '& .ql-editor p:last-child, & .ql-editor .thspian-lexical-p:last-child': { mb: 0 },
                    '& .ql-editor strong, & .ql-editor b, & .ql-editor .thspian-lexical-bold': {
                        fontWeight: 800,
                        color: OBSIDIAN.onSurface,
                    },
                    '& .ql-editor em, & .ql-editor i, & .ql-editor .thspian-lexical-italic': {
                        fontStyle: 'italic',
                    },
                    '& .ql-editor h1, & .ql-editor h2, & .ql-editor h3': {
                        color: OBSIDIAN.onSurface,
                        fontWeight: 800,
                        lineHeight: 1.3,
                        mt: 2,
                        mb: 1,
                    },
                    '& .ql-editor ul, & .ql-editor .thspian-lexical-ul, & .ql-editor ol, & .ql-editor .thspian-lexical-ol':
                        {
                            m: 0,
                            my: 1.5,
                            pl: 2.75,
                            listStylePosition: 'outside',
                        },
                    '& .ql-editor ul, & .ql-editor .thspian-lexical-ul': {
                        listStyleType: 'disc',
                    },
                    '& .ql-editor ol, & .ql-editor .thspian-lexical-ol': {
                        listStyleType: 'decimal',
                    },
                    '& .ql-editor li, & .ql-editor .thspian-lexical-li': {
                        mb: 0.85,
                        pl: 0.25,
                        fontWeight,
                        fontSize: 'inherit',
                        lineHeight: 1.65,
                    },
                    '& .ql-editor li:last-child, & .ql-editor .thspian-lexical-li:last-child': { mb: 0 },
                    '& .ql-editor blockquote, & .ql-editor .thspian-lexical-quote': {
                        m: '1.5rem 0',
                        ml: 0,
                        mr: 0,
                        pl: 2,
                        pr: 0,
                        py: 0.25,
                        border: 'none',
                        borderLeft: `3px solid ${OBSIDIAN.primary}`,
                        borderRadius: 0,
                        bgcolor: 'transparent',
                        color: OBSIDIAN.onSurfaceVariant,
                        fontStyle: 'italic',
                        fontWeight: 500,
                    },
                    '& .ql-editor blockquote p, & .ql-editor .thspian-lexical-quote .thspian-lexical-p, & .ql-editor .thspian-lexical-quote p':
                        {
                            m: 0,
                            mb: 0,
                            fontStyle: 'italic',
                            fontWeight: 500,
                        },
                    '& a': { color: OBSIDIAN.primary },
                }}>
                <RichTextViewer content={html} textColor={color} />
            </Box>
        );
    }
    if (!fallback) return null;
    return (
        <Typography
            sx={{
                fontFamily: OBSIDIAN_BODY_FONT,
                fontSize,
                fontWeight,
                color,
                lineHeight: 1.7,
            }}>
            {fallback}
        </Typography>
    );
}
